import { enableInstanceMaintenance, getInstanceMaintenance, updateInstanceMaintenance } from "@/apis";
import { FormContainer } from "@/components/form/FormContainer";
import { useResourceOne, useResourceUpdate } from "@/hooks";
import { useFormatter } from "@/hooks/useFormatter";
import { useResourceNavigate } from "@/hooks/useResourceNavigate";
import { useResourceContext } from "@/layouts/ResourceContext";
import { UpdateWrapper } from "@/layouts/UpdateWrapper";
import { RESOURCE_TYPE_INSTANCES, RESOURCE_TYPE_USERS } from "@/resources-def";
import { Button, Space, notification } from "antd";
import { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from 'dayjs';

const RESOURCE = RESOURCE_TYPE_INSTANCES;

export const EnterMaintenanceEditor = ({id, goto}) => {
  const formRef = useRef();
  const { formatDateTime } = useFormatter();
  const navigate = useResourceNavigate();
  const navigateBack = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [maintenance, setMaintenance] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);

  const { tenantName } = useResourceContext();
  useEffect(() => {
    const getMaintenance = async () => {
      try {
        setIsLoading(true);
        const response = await getInstanceMaintenance(id);
        if(response.status != "Error") {
          setMaintenance(response);
          setIsUpdate(true);
        } else {
          setIsUpdate(false);
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };
    getMaintenance();
  }, [id]);

  const rawSchema = [
    {
      i18nTitle: "action.instance.maintenance.subject",
      title: "Subject",
      dataIndex: "subject",
      width: "md",
      formItemProps: {
        rules: [
          {
            required: true,
            message: `Subject is required.`,
          },
        ]
      }
    },
    {
      i18nTitle: "action.instance.maintenance.reasone",
      title: "Reasone",
      dataIndex: "reason",
      width: "md",
      valueType:  "textarea",
      formItemProps: {
        rules: [
          {
            required: true,
            message: `Maintenance Reason is required.`,
          },
        ]
      }
    },
    {
      i18nTitle: "action.instance.maintenance.starttime",
      title: "Maintenance Start Time",
      dataIndex: "start_time",
      width: "md",
      valueType: "dateTime",
      fieldProps: {
        showTime: true
      },
      render: (value) => formatDateTime(value),
      formItemProps: {
        rules: [
          {
            required: true,
            message: `Start time is required.`,
          },
        ]
      }
    },
    {
      i18nTitle: "action.instance.maintenance.endtime",
      title: "Maintenance End Time",
      dataIndex: "end_time",
      valueType: "dateTime",
      width: "md",
      fieldProps: {
        showTime: true
      },
      render: (value) => formatDateTime(value),
      formItemProps: {
        rules: [
          {
            required: true,
            message: `End time is required.`,
          },
        ]
      }
    },
    {
      i18nTitle: "action.instance.maintenance.desc",
      title: "Description",
      dataIndex: "description",
      width: "md",
      valueType:  "textarea"
    }
  ];

  const doCommit = async (value) => {
    try {
      const reqObj = {...value, 
        start_time: dayjs(value.start_time).toISOString(true).replace(/\.\d{3}Z$/, 'Z'),
        end_time: dayjs(value.end_time).toISOString(true).replace(/\.\d{3}Z$/, 'Z')
      }
      const commitFunc = isUpdate? updateInstanceMaintenance : enableInstanceMaintenance;
      setIsSaving(true);
      const response = await commitFunc(id, reqObj); 
      setIsSaving(false);
      if (response.status == "OK") {
        notification.success({
          description: 'Configure maintenance successfully!'
        });
        navigate({ resource: RESOURCE, action: "list" });
      } else {
        notification.error({
          description: 'Configure maintenance failed!'
        });
      }       
    } catch (error) {
      console.error("Failed to configure maintenance:", error);
    }
  };

  return (
    <UpdateWrapper
      i18nTitle={isUpdate ? 'actions.updateMaintenance': 'actions.enableMaintenance'}
      footer={
        <Space>
          <Button
            onClick={() => {
              navigate({ resource: RESOURCE, action: "list" });
            }}
          >
            Cancel
          </Button>
          <Button loading={isSaving} type="primary" onClick={() => formRef.current.submit()}>
            Save
          </Button>
        </Space>
      }
    >
      {isLoading && <div>Loading...</div>}
      {!isLoading && (
        <FormContainer
          formRef={formRef}
          initValue={maintenance || {}}
          schema={rawSchema}
          onFinishCallback={doCommit}
          style={{ paddingTop: 24 }}
        />
      )}
    </UpdateWrapper>
  );
};
